import { getRawNumericString } from "../../../features/shared/utils"
import { DataStatus } from "../../enums"
import {
  PhoneEmailFax,
  Practice,
  PracticeAddress,
  PracticeContact,
} from "../schema"
import { PatchBasePracticeArgs } from "./practiceInformation"

export const getApplicablePracticeContacts = (
  contacts: PhoneEmailFax[],
  required = true,
) => {
  if (contacts.length === 0) {
    return contacts
  }
  const nonEmptyContacts = contacts.filter((contact) => {
    return contact.value && contact.value.trim() != ""
  })
  return required && nonEmptyContacts.length === 0
    ? [contacts[0]]
    : nonEmptyContacts
}

export const createBasePracticePayload = (payload: Partial<Practice>) => {
  return {
    name: payload.name,
    websiteUrl: payload.websiteUrl,
    overview: payload.overview,
    isVisible: payload.isVisible,
  }
}

export const createPatchBasePracticeArgs = (
  practiceId: string,
  payload: Partial<Practice>,
  currentPractice: Practice,
  setDataStatus?: (status: DataStatus) => void,
): PatchBasePracticeArgs => {
  return {
    practiceId,
    payload: {
      name: payload.name ?? currentPractice.name,
      websiteUrl: payload.websiteUrl,
      overview: payload.overview,
      isVisible: payload.isVisible ?? currentPractice.isVisible,
    },
    setDataStatus,
  }
}

export const createBillingAddressPayload = (
  practiceAddress: PracticeAddress,
) => {
  return {
    line1: practiceAddress.addressOne,
    line2: practiceAddress.addressTwo,
    city: practiceAddress.city,
    postalCode: practiceAddress.zipCode,
    county: practiceAddress.county,
    stateOrTerritory: practiceAddress.stateOrTerritory,
  }
}

export const createContactInfoPayload = (practiceContact: PracticeContact) => {
  let phoneList: PhoneEmailFax[] = getApplicablePracticeContacts(
    practiceContact.phones,
  )
  let faxList: PhoneEmailFax[] = getApplicablePracticeContacts(
    practiceContact.faxes,
    false,
  )
  let emailList: PhoneEmailFax[] = getApplicablePracticeContacts(
    practiceContact.altEmails,
    false,
  )

  // Set the first of each phone and fax as primary if not already set
  if (phoneList.length > 0 && !phoneList[0].isPrimary) {
    phoneList = [{ ...phoneList[0], isPrimary: true }, ...phoneList.slice(1)]
  }
  if (faxList.length > 0 && !faxList[0].isPrimary) {
    faxList = [{ ...faxList[0], isPrimary: true }, ...faxList.slice(1)]
  }
  emailList = [
    {
      // TODO: should we be setting an ID here? Shouldn't the backend provide this?
      id: crypto.randomUUID(),
      type: "email-all",
      value: practiceContact.primaryEmail,
      isPrimary: true,
      error: false,
    },
    ...emailList,
  ]

  return {
    emails: emailList.map((e) => ({
      contactType: e.type as string,
      address: e.value,
      isPrimary: e.isPrimary,
    })),
    phones: phoneList.map((p) => ({
      phoneType: p.type as string,
      number: getRawNumericString(p.value),
      isPrimary: p.isPrimary,
    })),
    faxes: faxList.map((f) => ({
      contactType: f.type as string,
      number: getRawNumericString(f.value),
      isPrimary: f.isPrimary,
    })),
  }
}
